import React, { useEffect, useCallback, useReducer } from 'react'
import { ImageWrapper, Placeholder, StyledImage, FallbackImage } from './styles'
import { isNavigatorOnline } from '../../Utils'
import { addToImgCache, getFromImgCache, ImageProps } from '../../Utils'
import { useImageIntersectionObserver } from '../../Hooks/Observers/useImageIntersectionObserver'
import { imageReducer, initialState } from './imageReducer'


export const Img: React.FC<ImageProps> = ({ src, alt, className }) => {
  const [state, dispatch] = useReducer(imageReducer, initialState)
  const { status, resolvedImageUrl, isOnline } = state

  useEffect(() => {
    dispatch({ type: 'RESET', payload: { src, isOnline: isNavigatorOnline() } })
  }, [src])

  useEffect(() => {
    const handleOnline = () => dispatch({ type: 'SET_ONLINE', payload: true })
    const handleOffline = () => dispatch({ type: 'SET_ONLINE', payload: false })

    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)

    return () => {
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('offline', handleOffline)
    }
  }, [])

  const loadImage = useCallback(async () => {
    if (!src || status !== 'PENDING') {
      if (status === 'PENDING' && !isOnline) {
        dispatch({ type: 'SET_ONLINE', payload: false })
      }
      return
    }

    dispatch({ type: 'LOAD_INITIATED' })

    if (src.startsWith('data:')) {
      dispatch({ type: 'LOAD_SUCCESS', payload: src })
      return
    }

    if (!isOnline) {
      dispatch({ type: 'SET_ONLINE', payload: false })
      return
    }

    try {
      const cachedResponse = await getFromImgCache(src)
      if (cachedResponse) {
        const blob = await cachedResponse.blob()
        const objectURL = URL.createObjectURL(blob)
        dispatch({ type: 'LOAD_SUCCESS', payload: objectURL })
        return
      }

      const response = await fetch(src)
      if (!response.ok) {
        throw new Error(
          `Failed to fetch image: ${response.statusText} (status: ${response.status})`
        )
      }
      addToImgCache(src, response.clone())
      const blob = await response.blob()
      const objectURL = URL.createObjectURL(blob)
      dispatch({ type: 'LOAD_SUCCESS', payload: objectURL })
    } catch (error) {
      console.error('Error loading image:', src, error)
      dispatch({ type: 'LOAD_FAILURE' })
    }
  }, [src, status, isOnline])

  useEffect(() => {
    if (status === 'PENDING' && isOnline) {
      loadImage()
    }
  }, [status, isOnline, loadImage])

  const handleIntersection = useCallback((isIntersecting: boolean) => {
    if (isIntersecting) {
      dispatch({ type: 'INTERSECTING' })
    }
  }, [])

  const wrapperRef = useImageIntersectionObserver(handleIntersection, {
    threshold: 0.1,
    rootMargin: '100px',
  })

  const showPlaceholder =
    status === 'IDLE' ||
    status === 'PENDING' ||
    status === 'LOADING' ||
    status === 'OFFLINE'
  const showImage = status === 'LOADED' && resolvedImageUrl
  const showErrorFallback = status === 'ERROR'

  return (
    <ImageWrapper
      ref={wrapperRef as React.RefObject<HTMLDivElement>}
      className={className}
    >
      {showPlaceholder && <Placeholder />}
      {showImage && <StyledImage src={resolvedImageUrl} alt={alt} />}
      {/* Fallback to standard img tag on error */}
      {showErrorFallback && (
        <FallbackImage src={src} alt={alt} className="image-fallback" loading="lazy" />
      )}
    </ImageWrapper>
  )
}
